import React, { useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin as GoogleOAuthLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';
import { btn, btnTone, input } from '../Kit';

/** The sign-in screen: the brand, one line of purpose, the Google button.
 *  Email sign-in stays one click away for accounts without Google. */
const GoogleLogin: React.FC = () => {
  const { login, loginWithPassword } = useAuth();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [withEmail, setWithEmail] = useState(false);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await loginWithPassword(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
    setSubmitting(false);
  };

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div className="relative min-h-screen flex flex-col items-center justify-center bg-warm-white px-4">
        <div className="w-full max-w-[340px] flex flex-col items-center text-center">
          <img src="/logo.png" alt="Bhuvana Fortune Greens" className="h-16 w-auto mb-6" />
          <h1 className="font-display text-[28px] font-semibold text-forest tracking-tight leading-tight">Marketing Dashboard</h1>
          <div className="w-10 h-[3px] bg-gold rounded-full mt-3 mb-4" />
          <p className="text-graphite text-[14px] mb-9">Every asset that brings a lead to the site.</p>

          <GoogleOAuthLogin
            onSuccess={(credentialResponse) => {
              if (credentialResponse.credential) {
                setError('');
                login(credentialResponse.credential).catch((err) =>
                  setError(err instanceof Error ? err.message : 'Login failed'),
                );
              }
            }}
            onError={() => {
              setError('Google sign-in failed. Try again.');
            }}
            theme="outline"
            size="large"
            text="signin_with"
            shape="rectangular"
          />

          {withEmail ? (
            <form onSubmit={handlePasswordLogin} className="w-full space-y-2.5 mt-8 pt-8 border-t border-charcoal/10">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="Email" autoComplete="email" autoFocus className={`${input} w-full h-10`} />
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder="Password" autoComplete="current-password" className={`${input} w-full h-10`} />
              <button type="submit" disabled={submitting} className={`${btn} ${btnTone.forest} w-full h-10`}>
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          ) : (
            <button type="button" onClick={() => setWithEmail(true)}
                    className="mt-6 text-[13px] font-semibold text-graphite hover:text-forest underline-offset-2 hover:underline">
              Sign in with email instead
            </button>
          )}
          {error && <p className="mt-4 text-[13px] text-brick">{error}</p>}
        </div>
        <p className="absolute bottom-6 text-[11.5px] text-graphite/70">Bhuvana Fortune Greens · internal</p>
      </div>
    </GoogleOAuthProvider>
  );
};

export default GoogleLogin;
